import get from 'lodash/get';

import { Point, Transforms } from '@lumapps/wrex/slate';
import { findParent } from '@lumapps/wrex/slate/utils/findParent';
import { focusAt } from '@lumapps/wrex/slate/utils/focusAt';
import { getSiblingPath } from '@lumapps/wrex/slate/utils/getSibling';
import { isSelectionEmpty } from '@lumapps/wrex/slate/utils/isSelectionEmpty';
import type { WrexEditor } from '@lumapps/wrex/types';

import type { ColumnsEditor, ColumnsElement } from '../types';
import { createColumn } from './createColumn';
import { createColumns } from './createColumns';
import { isColumns } from './isColumns';

/**
 * Insert columns after editor selection.
 * Returns the final insert position of the node. Useful for targeting the inserted node.
 */
export function insertColumns(
    editor: WrexEditor<ColumnsEditor>,
    attributes: Partial<ColumnsElement> & { number: number },
    point?: Point | undefined,
) {
    const { number = 2, ...restOfAttributes } = attributes;
    const columnsAttributes = { ...restOfAttributes };

    const at = point || editor.selection?.focus;
    const firstPathNumber = [get(at, ['path', 0]) as number];
    const after = getSiblingPath(firstPathNumber, 'after');
    const columnsNodeParent = findParent(editor, at?.path, isColumns);

    const canInsertOnSelection = at && isSelectionEmpty(editor, at.path) && !columnsNodeParent;

    const columnsNode = createColumns(
        columnsAttributes,
        [...Array(number)].map(() => createColumn()),
    );

    Transforms.insertNodes(editor, columnsNode, {
        at: canInsertOnSelection ? firstPathNumber : after ?? [editor.children.length],
    });

    // first node at the root of document (columns)
    const path = canInsertOnSelection ? firstPathNumber : after ?? [editor.children.length - 2];

    // path for the first column in the columns container
    const firstColPath = [...path, 0, 0, 0];

    focusAt(editor, firstColPath);
    return path;
}
